<template>
  <menu-bar class="pagination" role="menubar">
    <div class="flex items-center">
      <span>{{ total }} Einträge</span>
    </div>
    <div class="flex items-center gap-x-2">
      <div
        v-if="internalPage > 1"
        class="pr-3 cursor-pointer select-none"
        @click="setPage(internalPage-1)"
      >
        <chevron class="rotate-90 w-4 h-4" />
      </div>
      <div v-if="!listedPages.includes(1)">
        <button
          class="text-nav rounded-8 py-1 px-3"
          role="menuitem"
          @click="setPage(1)"
        >
          {{ 1 }}
        </button>
        <span class="text-nav rounded-8 py-1 px-3">
          ...
        </span>
      </div>
      <button
        v-for="listedPage in listedPages"
        :key="listedPage"
        class="text-nav rounded-8 py-1 px-3"
        :class="{ active: internalPage === listedPage }"
        role="menuitem"
        @click="setPage(listedPage)"
      >
        {{ listedPage }}
      </button>
      <div v-if="!listedPages.includes(numPages)">
        <span class="text-nav rounded-8 py-1 px-3">
          ...
        </span>
        <button
          class="text-nav rounded-8 py-1 px-3"
          role="menuitem"
          @click="setPage(numPages)"
        >
          {{ numPages }}
        </button>
      </div>
      <div
        v-if="internalPage < numPages"
        class="pr-3 cursor-pointer select-none"
        @click="setPage(internalPage+1)"
      >
        <chevron class="-rotate-90 w-4 h-4 ml-5.5" />
      </div>
    </div>
    <div class="flex ml-auto text-body-regular font-normal gap-x-9.5 items-center">
      <span class="flex-1 whitespace-nowrap">{{ $t("pagination.itemsPerPage") }}</span>
      <multiselect
        :model-value="internalPerPage"
        mode="single"
        :options="perPageChoices"
        :searchable="false"
        :hide-selected="true"
        :can-clear="false"
        :can-deselect="false"
        open-direction="top"
        select-label=""
        @update:model-value="updatePerPage"
      />
    </div>
  </menu-bar>
</template>

<script lang="ts" setup>
import { computed, unref } from 'vue';
import { useVModel } from '@vueuse/core';
import Chevron from '@images/chevron.svg?component';
import Multiselect from '@components/utils/Multiselect.vue';
import MenuBar from '../MenuBar.vue';

const emit = defineEmits(['update:page', 'update:perPage']);
const props = defineProps({
  total: { type: Number, default: 0 },
  page: { type: Number, default: 1 },
  perPage: { type: Number, default: 10 },
  perPageChoices: { type: Array, default: () => [5, 10, 50, 100] },
  shownPages: { type: Number, default: 3 },
});

const internalPerPage = useVModel(props, 'perPage', emit, { defaultValue: 10, passive: true });
const internalPage = useVModel(props, 'page', emit, { passive: true });

const numPages = computed(() => Math.ceil(props.total / unref(internalPerPage)) || 1);
const listedPages = computed(() => {
  const pages = unref(numPages);
  const currentPage = unref(internalPage);
  const visiblePages = Math.min(pages, props.shownPages);

  // Calculate the lastmost and rightmost page
  const firstPage = currentPage - 1 - Math.floor(visiblePages / 2);
  const lastPage = firstPage + visiblePages;

  let offset = 0;
  if (firstPage < 0) {
    offset = Math.abs(firstPage);
  }
  if (lastPage >= pages) {
    offset = pages - lastPage;
  }

  return Array.from({ length: visiblePages }, (_, i) => offset + firstPage + i + 1);
});
const setPage = (page: number) => {
  internalPage.value = page;
};

const updatePerPage = (perPage: number) => {
  internalPage.value = 1;
  internalPerPage.value = perPage;
};
</script>

<style lang="scss" scoped>

.pagination {
  @apply grid;
}

svg {
  @apply w-4 h-4 fill-current stroke-0 inline-block;

  :deep(path) {
    @apply fill-current;
  }
}

button {
  &.active, &:hover {
    @apply bg-info-blue text-white;
  }

  &:hover {
    @apply transition-opacity opacity-80;
  }
}

.grid {
  grid-template-columns: 1fr auto 1fr;

  & > :first-child {
    grid-column: 1;
  }
  & > :last-child {
    grid-column: 3;
  }
}
</style>
